import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import Divider from '@material-ui/core/Divider'
import Chip from '@material-ui/core/Chip'
import React, { useEffect, useMemo, useState } from 'react'
import { Button } from '../../../../components/common/Buttons'
import Typography from '../../../../components/common/Typography'
import { useAlertContext } from '../../../../contexts/AlertContext'
import connect from '../../connect'

const DEFAULT_EMAIL_ROUTING = {
  stripeNotificationsEmailRecipients: [],
  createQuoteRequestEmailRecipients: [],
  fileUploadNotificationsEmailRecipients: [],
  generalSupportEmailRecipients: [],
  sapFormSubmissionsEmailRecipients: [],
  samFormSubmissionsEmailRecipients: [],
  contractorPreQualificationFormSubmissionsEmailRecipients: [],
  gsaQualificationFormSubmissionsEmailRecipients: [],
}

const GENERAL_EMAIL_ROUTING_FIELDS = [
  {
    field: 'stripeNotificationsEmailRecipients',
    label: 'Stripe Quote Request Notifications',
  },
  {
    field: 'createQuoteRequestEmailRecipients',
    label: 'Quote Request Notifications',
  },
]

const FORM_EMAIL_ROUTING_FIELDS = [
  {
    field: 'sapFormSubmissionsEmailRecipients',
    label: 'SAP Form',
  },
  {
    field: 'samFormSubmissionsEmailRecipients',
    label: 'SAM Form',
  },
  {
    field: 'contractorPreQualificationFormSubmissionsEmailRecipients',
    label: 'Contractor Pre-Qualification Form',
  },
  {
    field: 'gsaQualificationFormSubmissionsEmailRecipients',
    label: 'GSA Qualification Form',
  },
]

const DISABLED_BUTTON_COLOR = '#c4c4c4'
const SAVE_BUTTON_COLOR = '#17b471'

const validateEmail = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

const normalizeEmailRouting = value => {
  return Object.keys(DEFAULT_EMAIL_ROUTING).reduce((emailRouting, key) => {
    emailRouting[key] = Array.isArray(value?.[key]) ? value[key] : []

    return emailRouting
  }, {})
}

const getEmailLabel = email => {
  if (email && typeof email === 'object') {
    return String(email.email || email.value || '').trim()
  }

  return String(email || '').trim()
}

const MultiEmailInput = ({ label, value, onChange }) => {
  const [inputValue, setInputValue] = useState('')
  const [error, setError] = useState('')

  const addEmails = rawValue => {
    const emails = String(rawValue || '')
      .split(',')
      .map(getEmailLabel)
      .filter(Boolean)
      .map(email => email.toLowerCase())

    if (!emails.length) {
      return
    }

    const nextEmails = [...value]
    const existingEmails = new Set(nextEmails.map(email => email.toLowerCase()))

    for (const email of emails) {
      if (!validateEmail(email)) {
        setError(`${email} is not a valid email address`)
        return
      }

      if (existingEmails.has(email)) {
        setError(`${email} has already been added`)
        return
      }

      nextEmails.push(email)
      existingEmails.add(email)
    }

    onChange(nextEmails)
    setInputValue('')
    setError('')
  }

  const removeEmail = email => {
    onChange(value.filter(item => item !== email))
    setError('')
  }

  const onKeyDown = ev => {
    if (ev.key === 'Enter' || ev.key === 'Tab' || ev.key === ',') {
      if (inputValue.trim()) {
        ev.preventDefault()
        addEmails(inputValue)
        return
      }

      if (ev.key === ',') {
        ev.preventDefault()
      }
    }
  }

  const onPaste = ev => {
    const pastedValue = ev.clipboardData.getData('text')

    if (pastedValue.includes(',')) {
      ev.preventDefault()
      addEmails(pastedValue)
    }
  }

  const onBlur = () => {
    if (inputValue.trim()) {
      addEmails(inputValue)
    }
  }

  return (
    <div>
      <Typography
        weight="600"
        variant="body1"
        style={{
          marginBottom: '8px',
          color: '#62728f',
        }}
      >
        {label}
      </Typography>

      <div
        style={{
          minHeight: '44px',
          border: error ? '1px solid #d93025' : '1px solid #edf0f7',
          borderRadius: '8px',
          background: '#fafbff',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          padding: '4px',
        }}
      >
        {value.map(email => (
          <Chip
            key={email}
            label={email}
            onDelete={() => removeEmail(email)}
            deleteIcon={
              <span
                style={{
                  color: '#fff',
                  fontSize: '16px',
                  paddingRight: '8px',
                }}
              >
                ×
              </span>
            }
            style={{
              margin: '4px',
              background: '#405d91',
              color: '#fff',
              borderRadius: '5px',
            }}
          />
        ))}

        <input
          value={inputValue}
          onChange={ev => setInputValue(ev.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onBlur={onBlur}
          placeholder={value.length ? 'Add another...' : 'Enter email address...'}
          aria-label={label}
          style={{
            flex: 1,
            minWidth: '160px',
            height: '34px',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            padding: '0 4px',
            fontSize: '14px',
          }}
        />
      </div>

      <Typography
        variant="body2"
        style={{
          marginTop: '8px',
          color: error ? '#d93025' : '#666',
        }}
      >
        {error || 'Press Enter, Tab, or comma to add an email'}
      </Typography>
    </div>
  )
}

const EmailRoutingConfiguration = ({
  emailRouting,
  saveEmailRouting,
}) => {
  const { successAlert, errorAlert } = useAlertContext()
  const [isSend, setIsSend] = useState(false)
  const [form, setForm] = useState(DEFAULT_EMAIL_ROUTING)
  const [initialForm, setInitialForm] = useState(DEFAULT_EMAIL_ROUTING)

  useEffect(() => {
    const nextForm = normalizeEmailRouting(emailRouting)

    setForm(nextForm)
    setInitialForm(nextForm)
  }, [emailRouting])

  const hasChanges = useMemo(() => {
    return JSON.stringify(form) !== JSON.stringify(initialForm)
  }, [form, initialForm])

  const saveDisabled = isSend || !hasChanges

  const onChangeField = field => value => {
    setForm({
      ...form,
      [field]: value,
    })
  }

  const saveSettings = async () => {
    try {
      setIsSend(true)
      await saveEmailRouting(form)
      setInitialForm(form)
      setIsSend(false)
      successAlert('Email routing settings saved successfully')
    } catch (error) {
      setIsSend(false)
      errorAlert('Email routing settings failed to save')
    }
  }

  const renderField = item => (
    <Grid key={item.field} item xs={12} md={6}>
      <MultiEmailInput
        label={item.label}
        value={form[item.field]}
        onChange={onChangeField(item.field)}
      />
    </Grid>
  )

  return (
    <Paper
      elevation={1}
      style={{
        padding: '24px',
        marginBottom: '24px',
        borderRadius: '8px',
      }}
    >
      <Typography weight="600" variant="h4" fontFamily="lexend">
        Email Routing
      </Typography>

      <Typography
        variant="body1"
        style={{
          marginTop: '8px',
          color: '#666',
        }}
      >
        Configure email addresses for routing different inquiries and notifications.
      </Typography>

      <Divider style={{ margin: '18px 0 24px' }} />

      <Grid container spacing={3}>
        {GENERAL_EMAIL_ROUTING_FIELDS.map(renderField)}
      </Grid>

      <Typography
        weight="600"
        variant="h5"
        fontFamily="lexend"
        style={{
          marginTop: '32px',
          marginBottom: '18px',
        }}
      >
        Form Submissions
      </Typography>

      <Grid container spacing={3}>
        {FORM_EMAIL_ROUTING_FIELDS.map(renderField)}
      </Grid>

      <Divider style={{ margin: '24px 0 18px' }} />

      <Button
        variant="contained"
        style={{
          background: saveDisabled ? DISABLED_BUTTON_COLOR : SAVE_BUTTON_COLOR,
          color: '#fff',
          cursor: saveDisabled ? 'not-allowed' : 'pointer',
        }}
        disabled={saveDisabled}
        pending={isSend}
        onClick={saveSettings}
      >
        Save Settings
      </Button>
    </Paper>
  )
}

export default connect(EmailRoutingConfiguration)